// SUB-OFFICE 7 — THE GAMEPLAY DIRECTOR. Owns the session from the ticket to
// the purge: intake (M9), the compose & submit chain (M6), the adjudication
// ceremony hand-off (M7), and the docket loop (M8). Everything stateful about
// "what the player is doing" lives here; the FSM narrates it; the server
// remains authoritative for docket/standing/grade.

import { FORM99V_WINDOW, INTERACTABLES, TURNSTILE, DESKS, LECTERN, TUBE_MOUTH } from './world/layout';
import { adoptIdentity, getUuid, loadProfile, replaceProfile, resolveIdentity, saveProfile, type Profile } from './identity';
import { api, type ApiResult } from './net';
import { ComposeOverlay } from './ui/compose';
import { VerdictSlipUi } from './ui/verdict';
import { holdSlamButton, printLayer } from './ui/chrome';
import { AdjudicationCeremony } from './ceremony/adjudication';
import { CeremonyDirector, delay } from './ceremony/director';
import {
  certificateCeremony, designationCeremony, docketClearedCeremony, form1Ceremony,
  form3Ceremony, gradeChangeCeremony, personnelReviewLetter, purgeCeremony,
  reissueCeremony, ticketCeremony, type CeremonyDeps,
} from './ceremony/scripted';
import { GRADE_NAMES } from '../shared/copy';
import { PhysicalBench, type BenchDeps } from './physical/bench';
import { offerRoom0 } from './physical/exercise0';
import type { ArrivalController } from './world/zones/arrival';
import type { VerticalCoreController } from './world/zones/verticalcontroller';
import { openCasefile } from './ui/casefile';
import { openDailyBoard, openRecordReader } from './ui/record';
import type { EventBus, Entity } from './entities/entities';
import type { Fsm } from './state';
import type { Player } from './entities/player';
import type { EyeLamps } from './entities/lamps';
import type { Canisters } from './entities/canisters';
import type { SceneManager } from './render/scene';
import type { PostPipeline } from './render/post';
import type { DomUi } from './ui/dom';
import type { BureauAudio } from './audio';
import type {
  CeremonyTier, Docket, DocketResponse, DocketSlot, SubmitResponse, Telemetry, Verdict,
} from '../shared/types';

const FORM99V_DEMAND =
  'FORM 99-V (PETITION TO CEASE PROCESSING): State why the Bureau should cease processing you. The Bureau will read it. The Bureau decides when you are finished.';

/** Window assignment for writing slots (Window 4 is the FORM 99-V slot). */
const SLOT_WINDOW: Record<number, number> = { 0: 1, 1: 2, 2: 3, 3: 3, 4: 5 };

interface SubmitChain {
  slot: DocketSlot;
  text: string;
  telemetry: Telemetry;
  completed?: boolean;
}

export class Game implements Entity {
  readonly id = 'game';

  private profile: Profile;
  private designation = '';
  private standing = 100;
  private grade = 'PERSON (PROVISIONAL)';
  private docket: Docket | null = null;
  private expeditedEligible = false;
  private industrialLine: string | null = null;
  private probation = 0;

  private activeSlot: DocketSlot | null = null;
  private summonedWindow = -1;
  private chain: SubmitChain | null = null;
  private drafts = new Map<string, string>();
  private busy = false; // a modal ceremony is running
  private absenceLocked = false;
  private voidFile = false;
  private awaitCopier = false;
  private intakePhase: 'ticket' | 'form3' | 'form1' | null = null;
  private firstDocket = false;

  private compose: ComposeOverlay;
  private slip: VerdictSlipUi;
  private adjudication: AdjudicationCeremony;
  private director = new CeremonyDirector();
  private deps: CeremonyDeps;
  private bench: PhysicalBench;
  private benchDeps: BenchDeps;
  private room0: { seed: string; variant: string; window_s: number } | null = null;
  private drawerGlow = false;
  private lastVerdictId: string | null = null;
  // The boot-only Arrival Exterior (M-A). Injected by boot after construction;
  // the new / reissue / 72h-absence paths route the walk-in through it.
  private arrival: ArrivalController | null = null;
  // The vertical circulation core (M-B): the lift lobby + cab spine off Intake.
  // Injected by boot after construction; entered via the working lift door.
  private vertcore: VerticalCoreController | null = null;

  constructor(
    private bus: EventBus,
    private fsm: Fsm,
    private sceneMgr: SceneManager,
    private post: PostPipeline,
    private player: Player,
    lamps: EyeLamps,
    private canisters: Canisters,
    private dom: DomUi,
    private audio: BureauAudio
  ) {
    printLayer(); // styles + root exist before any ceremony needs them
    this.profile = loadProfile();
    this.compose = new ComposeOverlay(bus, sceneMgr);
    this.slip = new VerdictSlipUi(bus);
    this.adjudication = new AdjudicationCeremony(bus, sceneMgr, post, lamps, canisters, player);
    this.deps = { bus, sceneMgr, player, director: this.director };
    this.benchDeps = { bus, player, sceneMgr, fsm };
    this.bench = new PhysicalBench(this.benchDeps);

    bus.on('interact', (p) => void this.onInteract((p as { id: string }).id));
    bus.on('window:activate', (p) => void this.onWindow((p as { idx: number }).idx));
    bus.on('window:printed', () => this.onFormPrinted());
    bus.on('escape', () => this.onEscape());
    window.addEventListener('keydown', (e) => {
      if (e.code === 'KeyH' && !textFocused()) this.toggleHaste();
    });
  }

  // ---------------------------------------------------------------------------
  // Boot: the hall has assembled; decide which door the session enters through
  // ---------------------------------------------------------------------------

  /** boot injects the Arrival Exterior controller once the scene rig exists. */
  setArrival(a: ArrivalController): void {
    this.arrival = a;
  }

  /** boot injects the vertical-core controller (the lift lobby + cab spine). */
  setVerticalCore(v: VerticalCoreController): void {
    this.vertcore = v;
  }

  async enterWorld(): Promise<void> {
    await resolveIdentity();
    this.profile = loadProfile();
    if (this.profile.designation) {
      this.player.setDesignation(this.profile.designation, `STANDING ${this.profile.standing ?? '—'}`);
      this.designation = this.profile.designation;
    }
    // returning files are already designated: drop straight into first-person
    if (this.profile.form1_done) this.bus.emit('fp:unlock', {});
    if (!this.profile.form1_done) {
      // The opening (and the reissued successor's re-boot): the game begins
      // OUTSIDE. Walk the lot, cross the door, THEN the intake beat commences —
      // exactly as before from the threshold in. Returning files skip all this.
      const startIntake = (): void => {
        this.fsm.to('INTAKE_FIRST');
        this.intakePhase = this.profile.ticket_no ? 'form3' : 'ticket';
        this.bus.emit('context', this.intakePhase === 'ticket'
          ? 'FOLLOW THE ARROWS. THE DISPENSER IS LIT. TAKE A TICKET.'
          : 'FORM 3 AWAITS AT THE LECTERN INSIDE THE ROPES.');
      };
      if (this.arrival) this.arrival.enter('first', startIntake);
      else startIntake();
      return;
    }
    await this.openSession();
  }

  /** Session open for a returning file: docket, absence, notifications. */
  private async openSession(): Promise<void> {
    const res = await api.docket();
    if (res.status === 401 || res.status === 404) {
      // the server does not know this file: intake again, from the top
      this.profile = saveProfile({ form1_done: false, intake_done: false, ticket_no: undefined });
      this.fsm.to('INTAKE_FIRST');
      this.intakePhase = 'ticket';
      this.bus.emit('context', 'THE DISPENSER IS LIT. TAKE A TICKET.');
      return;
    }
    if (res.status === 409) {
      // enrolled but Form 1 unfiled (also the reissued successor's state:
      // the server demands the copy restate its purpose — noted as friction,
      // played as fiction)
      this.dom.setDocket(null);
      this.profile = saveProfile({ form1_done: false });
      // the reissued successor walks in from the lot as #NNNN-B (the copier's
      // cruelest output is the walk back in); a plain enrolled-but-unfiled file
      // arrives the same way.
      const startForm1 = (): void => {
        this.fsm.to('INTAKE_FIRST');
        this.intakePhase = 'form1';
        this.bus.emit('context', 'FORM 1 REMAINS UNFILED FOR THIS COPY. THE FORM 1 DESK AWAITS.');
      };
      if (this.arrival) this.arrival.enter('first', startForm1);
      else startForm1();
      return;
    }
    const d = res.data;
    if (!d) {
      this.bus.emit('toast', res.memo || 'RECORDS ROOM TEMPORARILY SEALED.');
      this.fsm.to('ROAM');
      return;
    }
    if (d.void) {
      this.enterVoid(d.memo || 'This file is VOID. A successor can be arranged at the copier.');
      return;
    }
    this.absorbDocketResponse(d);

    if (d.absence_hours >= 72 && this.docket && this.docket.slots[0]?.type === 'ABS' &&
        this.docket.slots[0].status === 'PENDING') {
      // ratified ruling Q4: the 72h return re-uses the Arrival Exterior as a
      // DOWNGRADED walk of shame — no firstApproach prose, one absence citation
      // greeting your EXISTING designation, and FORM 7A-ABS waiting at the
      // turnstile INSIDE exactly as before once you cross.
      const startAbsence = (): void => {
        this.absenceLocked = true;
        this.fsm.to('ABSENCE_ACCOUNTING');
        if (d.absence_line) this.bus.emit('chyron', d.absence_line);
        this.bus.emit('context', 'THE TURNSTILE IS LOCKED. FORM 7A-ABS IS IN THE DOOR SLOT. E AT THE TURNSTILE.');
        setPrompt('turnstile', 'TURNSTILE — LOCKED — FORM 7A-ABS REQUIRED — E TO TAKE THE FORM');
      };
      if (this.arrival) this.arrival.enter('absence', startAbsence, d.absence_line || undefined);
      else startAbsence();
      return;
    }

    this.fsm.to('RETURN_GATE');
    if (d.absence_line) this.bus.emit('chyron', d.absence_line);
    else this.bus.emit('chyron', `THE TURNSTILE RECOGNIZES ${this.designation}.`);
    for (const line of d.notifications?.lines || []) this.bus.emit('toast', line);
    await delay(1200);
    await this.deliverPending(d.pending_verdicts);
    this.fsm.to('ROAM');
    this.advanceDocket();
  }

  private absorbDocketResponse(d: DocketResponse): void {
    this.designation = d.designation;
    this.standing = d.standing;
    this.grade = d.grade;
    this.syncAvatarGrade();
    this.probation = d.probation || 0;
    this.expeditedEligible = d.expedited_eligible;
    this.industrialLine = d.industrial ? (d.industrial_line || 'INDUSTRIAL ACTION') : null;
    this.docket = d.docket;
    this.firstDocket = !this.profile.intake_done;
    // the physical bench's ambient hooks
    this.room0 = d.room0_seeded || null;
    document.body.dataset.room0 = this.room0 ? '1' : '';
    if (this.room0) {
      setPrompt('door:form99', 'A DOOR, AJAR — A GREEN SEAM — E');
      this.bus.emit('chyron', 'A DOOR IN THE FORM-99 CORRIDOR IS NOT LOCKED. NO ONE HAS SAID SO. NO ONE WILL.');
    } else {
      setPrompt('door:form99', 'FORM-99 PROCESSING — E TO TRY IT');
    }
    if (d.drawer_glow && !this.drawerGlow) {
      this.drawerGlow = true;
      setPrompt('drawers', 'THE ARCHIVE — YOUR DRAWER IS LIT — E');
      this.bus.emit('toast', 'YOUR DRAWER IS LIT. SOMETHING WAS FILED WHILE YOU WERE ELSEWHERE.');
    }
    if (d.physical_bests_line) this.bus.emit('toast', d.physical_bests_line);
    this.post.setProbation(this.probation > 0 ? 1 : 0);
    this.player.setDesignation(this.designation, `STANDING ${this.standing}`);
    this.profile = saveProfile({
      designation: this.designation, standing: this.standing, grade: this.grade, form1_done: true,
    });
    this.renderStrip();
  }

  // ---------------------------------------------------------------------------
  // Interactions
  // ---------------------------------------------------------------------------

  private async onInteract(id: string): Promise<void> {
    if (this.busy || this.compose.isOpen) return;
    switch (id) {
      case 'dispenser':
        await this.takeTicket();
        return;
      case 'form3':
        await this.form3();
        return;
      case 'form1desk':
        await this.form1();
        return;
      case 'lectern':
        await this.lecternRitual();
        return;
      case 'tubemouth':
        await this.plunger();
        return;
      case 'copier':
        if (this.stationMatches('FORM5S')) { await this.runPhysical(this.activeSlot!); return; }
        await this.copier();
        return;
      case 'junction':
        if (this.stationMatches('FORM8R')) await this.runPhysical(this.activeSlot!);
        else this.bus.emit('chyron', 'THE SWITCHBOARD IS QUIET. THE TRUNK RELIEVES ITSELF, FOR NOW.');
        return;
      case 'junctiontap':
        // the grade-1 tap kept front-of-house (promotion precedent): it never
        // takes the shift — it points you to the full relief in the Mail Room.
        this.bus.emit('chyron', 'THE GRADE-1 TAP. THE TRUNK’S FULL RELIEF IS TAKEN IN THE MAIL ROOM.');
        return;
      case 'discwindow':
        if (this.stationMatches('FORM5D')) await this.runPhysical(this.activeSlot!);
        else this.bus.emit('chyron', 'THE DISCREPANCY WINDOW IS SHUTTERED. NO DOCUMENTS ARE IN DISPUTE.');
        return;
      case 'discwindowtap':
        this.bus.emit('chyron', 'INTAKE ONLY. FULL INSPECTION IS HELD ON INSPECTION ROW, BEHIND THE WINDOWS.');
        return;
      case 'terminals':
        if (this.stationMatches('FORM4T')) await this.runPhysical(this.activeSlot!);
        else this.bus.emit('chyron', 'THE TERMINAL BAY IS IDLE. THE UNITS SUFFER IN SILENCE, AS DESIGNED.');
        return;
      case 'armature':
        if (this.stationMatches('FORM12K')) await this.runPhysical(this.activeSlot!);
        else this.bus.emit('chyron', 'THE ARMATURE HOLDS ITS ALIGNMENT. THE STAMP STRIKES TRUE ENOUGH, FOR NOW.');
        return;
      case 'manifold':
        if (this.activeSlot?.type === 'PHYSICAL' && this.activeSlot.physical?.system === 'FORM2V') {
          await this.runPhysical(this.activeSlot);
        } else {
          this.bus.emit('chyron', 'THE MANIFOLD IDLES. THE VAULT DELIBERATES WITHOUT ASSISTANCE, FOR NOW.');
        }
        return;
      case 'door:form99':
        if (this.room0) {
          await this.enterRoom0();
          return;
        }
        return; // props speaks the sealed line
      case 'door:lift':
        // the working lift routes into the vertical core — but only once you
        // are a roaming, designated file (intake owns you until then).
        if (this.fsm.state === 'ROAM' && this.profile.form1_done && this.vertcore) {
          this.vertcore.enter();
        } else {
          this.bus.emit('chyron', 'THE CAB IS NOT FOR YOU YET. FINISH WHAT INTAKE ASKS. THE LIFT WILL STILL BE HERE. IT IS ALWAYS HERE.');
        }
        return;
      case 'drawers':
        await this.openDrawer();
        return;
      case 'gallery':
        this.busy = true;
        await openRecordReader(this.bus);
        this.busy = false;
        return;
      case 'board':
        this.busy = true;
        await openDailyBoard(this.bus, async (v) => {
          const undolly = v.ceremony === 'FULL' ? this.adjudication.dollyIn() : null;
          await this.slip.show(v, this.slipOpts(v.ceremony, 0, true));
          if (undolly) undolly();
          this.standing = v.standing;
          this.player.setDesignation(this.designation, `STANDING ${v.standing}`);
        });
        this.busy = false;
        return;
      case 'podiums':
        this.busy = true;
        await openDailyBoard(this.bus, async () => undefined);
        this.busy = false;
        return;
      case 'turnstile':
        if (this.absenceLocked) await this.absenceForm();
        return;
      case 'bench:4':
        await this.recess();
        return;
      default:
        if (id.startsWith('desk:')) await this.sitAtDesk(parseInt(id.split(':')[1], 10));
    }
  }

  private async onWindow(idx: number): Promise<void> {
    if (this.busy || this.compose.isOpen) return;
    // an active summons at this window: collect the form (or take the shift)
    if (this.activeSlot && this.summonedWindow === idx && this.fsm.state === 'SUMMONS') {
      if (this.activeSlot.type === 'PHYSICAL') {
        await this.runPhysical(this.activeSlot);
        return;
      }
      this.fsm.to('COLLECT');
      this.bus.emit('summons:clear');
      this.bus.emit('window:print', { windowIdx: idx, text: this.activeSlot.form_id });
      this.bus.emit('context', 'THE FORM IS PRINTING. FORMS CANNOT BE HURRIED.');
      return;
    }
    if (idx === FORM99V_WINDOW && (this.fsm.state === 'ROAM' || this.fsm.state === 'SUMMONS')) {
      await this.form99v();
      return;
    }
    this.bus.emit('chyron', `WINDOW ${idx} HAS NO SUMMONS FOR YOU. THE WINDOW HAS NOTED THE VISIT.`);
  }

  private onFormPrinted(): void {
    if (!this.activeSlot || this.fsm.state !== 'COLLECT') return;
    this.player.setCarry('paper');
    this.bus.emit('audio:tick');
    this.bus.emit('context', 'CARRY THE FORM TO ANY FREE DESK. E TO SIT.');
    for (let i = 0; i < DESKS.length; i++) setPrompt(`desk:${i}`, 'TYPEWRITER DESK — E TO SIT AND COMPOSE');
  }

  private onEscape(): void {
    // aborting the submit chain returns the form to hand
    if (this.fsm.state === 'SUBMIT_CHAIN' && this.chain) {
      this.drafts.set(this.chain.slot.demand_id, this.chain.text);
      this.chain = null;
      this.player.setCarry('paper');
      this.fsm.to('COLLECT');
      this.bus.emit('toast', 'The canister unrolls, reluctantly. The form returns to your hand.');
    }
  }

  private toggleHaste(): void {
    if (this.grade === 'PERSON (PROVISIONAL)' || !this.profile.form1_done) {
      this.bus.emit('toast', 'FORM 11 (AUTHORIZED HASTE) is issued at grade PROBATIONARY ASSET. Walk.');
      return;
    }
    this.player.haste = !this.player.haste;
    this.bus.emit('chyron', this.player.haste
      ? 'HASTE AUTHORIZED. DIGNITY WAIVED.'
      : 'HASTE WITHDRAWN. DIGNITY PARTIALLY RESTORED.');
  }

  // ---------------------------------------------------------------------------
  // M9 — INTAKE
  // ---------------------------------------------------------------------------

  private async takeTicket(): Promise<void> {
    if (this.profile.form1_done) {
      this.bus.emit('chyron', 'YOU HOLD A NUMBER ALREADY. THE DISPENSER REMEMBERS DISPENSING IT.');
      return;
    }
    this.busy = true;
    const res = await api.ticket(getUuid());
    if (!res.ok || !res.data) {
      this.busy = false;
      this.bus.emit('toast', res.memo || 'The dispenser jams, officially. Try again.');
      return;
    }
    const t = res.data;
    this.profile = saveProfile({
      ticket_no: t.ticket_no,
      designation: t.designation,
      standing: t.standing,
      grade: t.grade,
      ...(t.claim_phrase ? { claim_phrase: t.claim_phrase } : {}),
    });
    this.designation = t.designation;
    await ticketCeremony(this.deps, t);
    this.busy = false;
    this.intakePhase = 'form3';
    this.bus.emit('chyron', 'PROCEED THROUGH THE ROPES. FORM 3 PRECEDES EVERYTHING AUDIBLE.');
    this.bus.emit('context', 'FORM 3 — CONSENT TO AMBIENCE — AT THE LECTERN INSIDE THE ROPES.');
    setPrompt('dispenser', 'TICKET DISPENSER — YOUR NUMBER IS ISSUED');
  }

  private async form3(): Promise<void> {
    if (!this.profile.ticket_no) {
      this.bus.emit('chyron', 'TAKE A TICKET FIRST. THE DISPENSER IS BEHIND YOU AND HAS BEEN PATIENT.');
      this.bus.emit('context', 'THE TICKET DISPENSER IS LIT TO THE NORTH. TAKE A TICKET, THEN RETURN TO THE ROPES.');
      return;
    }
    this.busy = true;
    const accepted = await form3Ceremony(this.deps);
    this.audio.setConsent(accepted);
    this.profile = saveProfile({ audio_consent: accepted });
    this.busy = false;
    this.bus.emit('toast', accepted
      ? 'FORM 3 FILED. The hall becomes audible. It was always this loud.'
      : 'FORM 3 DECLINED. The hall continues without your hearing it.');
    if (!this.profile.form1_done) {
      this.intakePhase = 'form1';
      this.bus.emit('context', 'PROCEED TO THE FORM 1 DESK. REGISTRATION PRECEDES EVERYTHING ELSE.');
    }
  }

  private async form1(): Promise<void> {
    if (this.profile.form1_done) {
      this.bus.emit('chyron', 'FORM 1 IS ON FILE. FILING IT TWICE WOULD CREATE TWO OF YOU.');
      return;
    }
    if (!this.profile.ticket_no) {
      this.bus.emit('chyron', 'TAKE A TICKET FIRST. THE DISPENSER IS BEHIND YOU AND HAS BEEN PATIENT.');
      this.bus.emit('context', 'THE TICKET DISPENSER IS LIT TO THE NORTH. TAKE A TICKET, THEN RETURN.');
      return;
    }
    this.busy = true;
    const f = await form1Ceremony(this.deps);
    // the lever has been pulled: the POST races the ceremony floor.
    // Form 1 is never queued server-side (a first visit always receives a
    // verdict, deferred if it must be) — so the ceremony waits well past the
    // ordinary conversion window rather than issuing a chit at intake.
    this.fsm.to('ADJUDICATION_FULL');
    const request = api.form1(f.sentence, f.telemetry, f.dwellMs);
    const outcome = await this.adjudication.run('FULL', request, { windowS: 35 });
    if (outcome.kind !== 'result' || !outcome.response?.ok || !outcome.response.data?.verdict) {
      // recovery: the POST may have landed even though the client gave up
      // (or a prior attempt filed it) — the server refuses second first
      // impressions, so probe the docket instead of looping on the lever.
      const already = outcome.response?.data?.registered === true;
      const probe = already ? null : await api.docket();
      if (already || (probe && probe.status === 200 && probe.data && !probe.data.void)) {
        this.profile = saveProfile({ form1_done: true });
        this.bus.emit('toast', 'THE REGISTRAR FILED YOUR FORM WHILE YOU WAITED. THE FINDING RESTS IN YOUR FILE.');
        this.busy = false;
        this.intakePhase = null;
        await this.openSession();
        return;
      }
      this.busy = false;
      this.fsm.to('INTAKE_FIRST');
      this.bus.emit('toast', outcome.response?.memo || 'The Registrar will return. Your sentence is retained. Pull the lever again shortly.');
      return;
    }
    const r = outcome.response.data;
    const verdict = r.verdict as Verdict;
    this.designation = r.designation;
    this.standing = verdict.standing;
    this.grade = r.grade;
    this.syncAvatarGrade();
    this.profile = saveProfile({
      form1_done: true, designation: r.designation, standing: verdict.standing, grade: r.grade,
    });
    this.fsm.to('VERDICT');
    const undolly = this.adjudication.dollyIn();
    await this.slip.show(verdict, this.slipOpts('FULL', outcome.movedM, outcome.stoodStill));
    undolly();
    this.fsm.to('ACK');
    await designationCeremony(this.deps, this.designation, verdict.standing);
    this.player.setDesignation(this.designation, `STANDING ${verdict.standing}`);
    // the designation ceremony is complete: the intake lens gives way to FP
    this.bus.emit('fp:unlock', {});
    this.busy = false;
    this.intakePhase = null;
    // the first docket — three summonses are framed; five slots exist
    const d = await api.docket();
    if (d.ok && d.data && !d.data.void) {
      this.absorbDocketResponse(d.data);
      this.bus.emit('chyron', 'A DOCKET HAS BEEN COMPOSED FOR YOU. IT IS MODEST. SO ARE YOU.');
      this.fsm.to('ROAM');
      this.advanceDocket();
    } else {
      this.fsm.to('ROAM');
    }
  }

  // ---------------------------------------------------------------------------
  // M8 — THE DOCKET LOOP
  // ---------------------------------------------------------------------------

  /** Choose the next PENDING slot (first docket: denunciation front-loaded).
   *  The W1 bench is live: PHYSICAL slots route to their stations. */
  private nextSlot(): DocketSlot | null {
    if (!this.docket) return null;
    const pending = this.docket.slots.filter(
      (s) => s.status === 'PENDING' && s.type !== 'NOTARY'
    );
    if (!pending.length) return null;
    if (this.firstDocket) {
      const rank = (s: DocketSlot): number => {
        if (s.genre && /apolog/i.test(s.genre)) return 0; // an easy apology opens
        if (s.genre && /denunciation/i.test(s.genre)) return 1; // the funny one, front-loaded
        if (s.type === 'PHYSICAL') return 4; // the bench after the pen, first time
        if (s.type === 'RITUAL' || s.type === 'RECESS') return 5; // the civic act after
        if (s.type === 'REVIEW') return 9;
        return 2 + s.idx * 0.1; // remaining writing tasks, in docket order
      };
      return [...pending].sort((a, b) => rank(a) - rank(b))[0];
    }
    return pending[0];
  }

  private advanceDocket(): void {
    if (!this.docket) return;
    const slot = this.nextSlot();
    this.activeSlot = slot;
    this.renderStrip();
    if (!slot) return;
    switch (slot.type) {
      case 'FORM7A':
      case 'ABS':
      case 'AUDIT': {
        const w = SLOT_WINDOW[slot.idx] ?? 1;
        this.summonedWindow = w;
        this.bus.emit('summons', { windowIdx: w });
        this.fsm.to('SUMMONS');
        this.bus.emit('chyron', `WINDOW ${w} IS LIT FOR ${this.designation || 'THE PETITIONER'}. ${slot.form_id} AWAITS COLLECTION.`);
        this.bus.emit('context', `WINDOW ${w} IS LIT. COLLECT ${slot.form_id}. E AT THE WINDOW.`);
        setPrompt(`window:${w}`, `WINDOW ${w} — YOUR SUMMONS — E TO COLLECT THE FORM`);
        break;
      }
      case 'RITUAL':
        this.summonedWindow = -1;
        this.fsm.to('SUMMONS');
        this.bus.emit('chyron', `A RITUAL IS ASSIGNED: ${slot.form_id}. THE LECTERN IS WHERE RITUALS STAND.`);
        this.bus.emit('context', 'PROCEED TO THE LECTERN ON THE WRITING FLOOR. RITUALS ARE PERFORMED STANDING.');
        setPrompt('lectern', 'THE LECTERN — YOUR RITUAL AWAITS — E TO BEGIN');
        break;
      case 'RECESS':
        this.summonedWindow = -1;
        this.fsm.to('SUMMONS');
        this.bus.emit('chyron', 'MANDATORY RECESS IS ASSIGNED. THE BENCH IS PROVIDED. THE BUREAU WILL COUNT.');
        this.bus.emit('context', 'PROCEED TO THE RECESS ROOM. THE BENCH KNOWS WHY YOU ARE THERE.');
        setPrompt('bench:4', 'THE RECESS BENCH — YOUR REST IS ASSIGNED — E TO SIT');
        break;
      case 'REVIEW': {
        this.summonedWindow = 5;
        this.bus.emit('summons', { windowIdx: 5 });
        this.fsm.to('SUMMONS');
        this.bus.emit('chyron', 'PERSONNEL REVIEW. WINDOW 5. YOUR PRESENCE IS THE OFFERING.');
        this.bus.emit('context', 'WINDOW 5 IS LIT. PRESENT YOURSELF FOR REVIEW.');
        setPrompt('window:5', 'WINDOW 5 — PERSONNEL REVIEW — E TO PRESENT YOURSELF');
        break;
      }
      case 'PHYSICAL': {
        const p = slot.physical;
        if (!p) {
          this.fsm.to('ROAM');
          break;
        }
        this.summonedWindow = -1;
        this.fsm.to('SUMMONS');
        if (p.system === 'FORM5J') {
          this.summonedWindow = 2;
          this.bus.emit('summons', { windowIdx: 2 });
          this.bus.emit('chyron', `${p.form_no} — ${p.title}. A TRAY WAITS AT WINDOW 2.`);
          this.bus.emit('context', 'A TRAY OF STRANGERS AWAITS AT WINDOW 2. E TO TAKE THE SHIFT.');
          setPrompt('window:2', 'WINDOW 2 — THE CONCURRENCE TRAY — E TO BEGIN THE SHIFT');
        } else if (p.system === 'FORM2V') {
          this.bus.emit('chyron', `${p.form_no} — ${p.title}. REPORT TO THE GAUGE MANIFOLD ON THE VAULT FACE.`);
          this.bus.emit('context', 'PROCEED TO THE VAULT FACE. THE MANIFOLD REQUIRES HANDS.');
          setPrompt('manifold', 'THE GAUGE MANIFOLD — YOUR DUTY — E TO PLACE YOUR HANDS');
        } else if (p.system === 'FORM33C' || p.system === 'FORM5M' || p.system === 'FORM44S' || p.system === 'FORM17O') {
          // walking/carry/hold/shadow systems ignite in-world; the body IS the interaction
          void this.runPhysical(slot);
        } else if (p.system === 'FORM8R') {
          this.bus.emit('chyron', `${p.form_no} — ${p.title}. THE JUNCTION SWITCHBOARD IS LIVE IN THE MAIL ROOM.`);
          this.bus.emit('context', 'PROCEED TO THE JUNCTION SWITCHBOARD IN THE MAIL ROOM, PAST INTAKE. E TO TAKE THE RELIEF.');
          setPrompt('junction', 'THE JUNCTION SWITCHBOARD — YOUR RELIEF — E TO TAKE THE LEVERS');
        } else if (p.system === 'FORM5D') {
          this.bus.emit('chyron', `${p.form_no} — ${p.title}. THE DISCREPANCY WINDOW OPENS ON INSPECTION ROW.`);
          this.bus.emit('context', 'PROCEED TO INSPECTION ROW, BEHIND THE WINDOW ROW. E TO OPEN THE SHIFT.');
          setPrompt('discwindow', 'THE DISCREPANCY WINDOW — E TO OPEN THE SHIFT');
        } else if (p.system === 'FORM5S') {
          this.bus.emit('chyron', `${p.form_no} — ${p.title}. THE COPIER ROOM WARMS. THE TEETH ARE HUNGRY.`);
          this.bus.emit('context', 'PROCEED TO THE COPIER IN THE REGISTRY CORNER. E TO BEGIN RETENTION.');
          setPrompt('copier', 'THE COPIER — RETENTION DUTY — E TO BEGIN THE SHIFT');
        } else if (p.system === 'FORM4T') {
          this.bus.emit('chyron', `${p.form_no} — ${p.title}. A UNIT HAS FILED A GRIEVANCE AT THE ARCHIVE WALL.`);
          this.bus.emit('context', 'PROCEED TO THE TERMINAL SUPPORT BAY AT THE ARCHIVE WALL. E TO OPEN THE TICKET.');
          setPrompt('terminals', 'TERMINAL SUPPORT BAY — A TICKET IS OPEN — E TO ANSWER IT');
        } else if (p.system === 'FORM12K') {
          this.bus.emit('chyron', `${p.form_no} — ${p.title}. THE ARMATURE MUST BE TRUED BEFORE THE NEXT VERDICT.`);
          this.bus.emit('context', 'PROCEED TO THE ARMATURE STATION ON THE WINDOW ROW. E TO MOUNT THE DIALS.');
          setPrompt('armature', 'THE ARMATURE STATION — E TO MOUNT THE DIALS');
        } else {
          this.bus.emit('context',
            `${p.form_no} AWAITS EQUIPMENT THE HALL IS STILL INSTALLING. PROCEED WITH YOUR OTHER DUTIES.`);
          this.fsm.to('ROAM');
        }
        break;
      }
      case 'NOTARY':
        // NOTARY is cut in v1; nextSlot() never selects it.
        this.fsm.to('ROAM');
        break;
    }
  }

  // ---------------------------------------------------------------------------
  // The physical bench (W1): start -> run -> submit -> the ordinary slip
  // ---------------------------------------------------------------------------

  /** True when an assigned PHYSICAL slot at this station is awaiting the body. */
  private stationMatches(system: string): boolean {
    return (
      this.activeSlot?.type === 'PHYSICAL' &&
      this.activeSlot.physical?.system === system &&
      this.fsm.state === 'SUMMONS'
    );
  }

  private async runPhysical(slot: DocketSlot): Promise<void> {
    if (this.busy || this.bench.busy) return;
    this.busy = true;
    const outcome = await this.bench.run(slot);
    this.busy = false;
    this.bus.emit('summons:clear');
    if (outcome.kind === 'abandoned') {
      this.fsm.to('ROAM');
      this.bus.emit('toast', 'The duty remains open. Duties are patient. It is their defining quality.');
      this.advanceDocket();
      return;
    }
    if (outcome.kind === 'failed') {
      this.fsm.to('ROAM');
      this.bus.emit('toast', outcome.memo);
      this.advanceDocket();
      return;
    }
    const r = outcome.response;
    this.busy = true;
    await this.handleSubmitResponse(
      r, slot, r.verdict?.ceremony || 'EXPEDITED', 0, true
    );
  }

  private renderStrip(): void {
    if (!this.docket) {
      this.dom.setDocket(null);
      return;
    }
    this.dom.setDocket(
      this.docket.slots.map((s) => ({
        label: s.form_id.length > 22 ? s.form_id.slice(0, 22) : s.form_id,
        status: s.status === 'DONE' ? 'DONE' : this.activeSlot?.demand_id === s.demand_id ? 'ACTIVE' : 'PENDING',
      })),
      this.industrialLine
    );
  }

  // ---------------------------------------------------------------------------
  // M6 — COMPOSE & SUBMIT CHAIN
  // ---------------------------------------------------------------------------

  private async sitAtDesk(deskIdx: number): Promise<void> {
    if (!this.activeSlot || this.player.carrying !== 'paper' || this.fsm.state !== 'COLLECT') {
      this.bus.emit('chyron', 'THE DESK ACCEPTS ONLY PETITIONERS WITH FORMS. THE DESK IS STRICT ABOUT THIS.');
      return;
    }
    const slot = this.activeSlot;
    const desk = DESKS[deskIdx] || DESKS[0];
    this.fsm.to('COMPOSE');
    this.player.setCarry('none');
    const result = await this.compose.compose({
      formId: slot.form_id,
      demand: slot.demand_text,
      constraints: slot.constraints,
      designation: this.designation,
      mode: 'standard',
      demandId: slot.demand_id,
      draft: this.drafts.get(slot.demand_id),
      overheadAt: { x: desk.x, z: desk.z },
    });
    if (result.abandoned) {
      this.drafts.set(slot.demand_id, result.text);
      this.player.setCarry('paper');
      this.fsm.to('COLLECT');
      this.bus.emit('chyron', 'DRAFT ABANDONED. The Bureau read it anyway.');
      return;
    }
    this.drafts.set(slot.demand_id, result.text);
    this.chain = { slot, text: result.text, telemetry: result.telemetry };
    this.player.setCarry('canister');
    this.fsm.to('SUBMIT_CHAIN');
    this.bus.emit('audio:thunk');
    this.bus.emit('toast', 'The page rolls into a canister. The canister is patient. Carry it to the tube mouth.');
    this.bus.emit('context', 'CARRY THE CANISTER TO THE TUBE MOUTH. HOLD THE PLUNGER. RELEASE TO SUBMIT.');
    setPrompt('tubemouth', 'PNEUMATIC TUBE MOUTH — E TO MOUNT THE CANISTER');
  }

  private async plunger(): Promise<void> {
    if (!this.chain || this.fsm.state !== 'SUBMIT_CHAIN') {
      this.bus.emit('chyron', 'THE TUBE ACCEPTS CANISTERS. YOU ARE NOT CARRYING ONE. THE TUBE WAITS.');
      return;
    }
    this.busy = true;
    const layer = printLayer();
    const panel = document.createElement('div');
    panel.className = 'paper-modal slim';
    panel.innerHTML =
      `<div class="paper-head letterhead"><span>PNEUMATIC DISPATCH</span><span>${this.chain.slot.form_id}</span></div>` +
      `<div class="paper-body typewriter" style="font-size:12px">The canister is mounted. Hold the plunger; release to slam.` +
      (this.expeditedEligible ? '<br><span style="opacity:.7">Holding well past the catch requests formality. The Bureau will notice.</span>' : '') +
      `</div>`;
    const foot = document.createElement('div');
    foot.className = 'paper-foot';
    panel.appendChild(foot);
    layer.appendChild(panel);

    await new Promise<void>((resolve) => {
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'slam-btn secondary';
      back.textContent = 'NOT YET';
      back.addEventListener('click', () => {
        panel.remove();
        this.busy = false;
        resolve();
      });
      foot.appendChild(back);
      foot.appendChild(
        holdSlamButton('PLUNGER — HOLD, THEN RELEASE', (r) => {
          panel.remove();
          void this.fireSubmission(r.formality).then(resolve);
        }, { formalityHint: true })
      );
    });
  }

  /** t=0: the slam IS the POST. The ceremony masks everything after. */
  private async fireSubmission(formality: boolean): Promise<void> {
    if (!this.chain) return;
    const { slot, text, telemetry, completed } = this.chain;
    this.chain = null;
    this.player.setCarry('none');
    this.bus.emit('audio:thunk');
    if (formality) this.bus.emit('chyron', 'THE PETITIONER REQUESTS FORMALITY. GRANTED.');

    const tier: CeremonyTier = !this.expeditedEligible || formality ? 'FULL' : 'EXPEDITED';
    this.fsm.to(tier === 'FULL' ? 'ADJUDICATION_FULL' : 'ADJUDICATION_EXPEDITED');
    const request = api.submit({
      demand_id: slot.demand_id, text, telemetry,
      ...(completed !== undefined ? { completed } : {}),
      ...(formality ? { request_full: true } : {}),
    });
    const outcome = await this.adjudication.run(tier, request);

    if (outcome.kind === 'queued-timeout') {
      this.fsm.to('INDUSTRIAL_ACTION');
      await this.slip.showQueuedChit(
        'THE MACHINE IS CONSIDERING AT ITS OWN PACE. YOUR OFFERING IS FILED AND WILL BE JUDGED. THE VERDICT WILL FIND YOUR DRAWER.'
      );
      this.fsm.to('ROAM');
      this.busy = false;
      // the slot resolves server-side; refresh shortly and continue
      await delay(400);
      await this.refreshDocket();
      this.advanceDocket();
      return;
    }
    if (outcome.kind === 'failed' || !outcome.response) {
      this.returnChainToHand(slot, text, telemetry, 'The tube exhales. The hall is briefly unpersuaded. The canister returns to your hand.');
      return;
    }
    const res = outcome.response;
    if (res.status === 202 && res.data?.queued) {
      this.fsm.to('INDUSTRIAL_ACTION');
      if (res.data.docket) this.docket = res.data.docket;
      await this.slip.showQueuedChit(res.data.memo || 'INDUSTRIAL ACTION. YOUR OFFERING HAS BEEN QUEUED.');
      this.fsm.to('ROAM');
      this.busy = false;
      this.advanceDocket();
      return;
    }
    if (!res.ok || !res.data) {
      this.returnChainToHand(slot, text, telemetry, res.memo || 'The filing was declined at the mouth of the tube. It returns to your hand.');
      return;
    }
    await this.handleSubmitResponse(res.data, slot, tier, outcome.movedM, outcome.stoodStill, { text, telemetry });
  }

  private returnChainToHand(slot: DocketSlot, text: string, telemetry: Telemetry, memo: string): void {
    this.drafts.set(slot.demand_id, text);
    this.chain = { slot, text, telemetry };
    this.player.setCarry('canister');
    this.fsm.to('SUBMIT_CHAIN');
    this.busy = false;
    this.bus.emit('toast', memo);
  }

  // ---------------------------------------------------------------------------
  // Verdict handling (M7 tail)
  // ---------------------------------------------------------------------------

  private async handleSubmitResponse(
    r: SubmitResponse,
    slot: DocketSlot,
    tierPlayed: CeremonyTier,
    movedM: number,
    stoodStill: boolean,
    original?: { text: string; telemetry: Telemetry }
  ): Promise<void> {
    if (r.docket) this.docket = r.docket;

    if (r.verdict) {
      const v = r.verdict;
      this.lastVerdictId = v.verdict_id;
      // the server may demand reverence the client didn't stage
      if (v.ceremony === 'FULL' && tierPlayed === 'EXPEDITED') {
        this.fsm.to('ADJUDICATION_FULL');
        await this.adjudication.escalateToFull();
      }
      const tier = v.ceremony;
      if (v.stamp === 'UNFIT TRIBUTE') {
        // the window slams shut mid-transaction — the loudest sound in the game
        this.bus.emit('audio:unfit');
      }
      this.fsm.to('VERDICT');
      const undolly = tier === 'FULL' ? this.adjudication.dollyIn() : null;
      await this.slip.show(v, this.slipOpts(tier, movedM, stoodStill));
      if (undolly) undolly();
      this.fsm.to('ACK');
      this.standing = v.standing;
      this.grade = v.grade;
      this.syncAvatarGrade();
      this.profile = saveProfile({ standing: v.standing, grade: v.grade });
      this.player.setDesignation(this.designation, `STANDING ${v.standing}`);

      if (r.slot_pending && original) {
        // UNFIT: the slot stays open; resubmit as fiction. The draft survives.
        this.drafts.set(slot.demand_id, original.text);
        this.busy = false;
        this.fsm.to('COLLECT');
        this.player.setCarry('paper');
        this.bus.emit('context', 'THE SLOT REMAINS OPEN. RESUBMIT AS FICTION. THE DESK WAITS.');
        this.renderStrip();
        return;
      }
      if (v.grade_changed) await gradeChangeCeremony(this.deps, this.designation, v.grade);
      if (v.penance) {
        this.bus.emit('chyron',
          `PENANCE ASSIGNED: ${v.penance.form_no}, THE ROUTE OF CONTRITION. IT RIDES YOUR DOCKET AS A SIXTH DUTY.`);
      }
      if (v.purged) {
        await this.enterPurge();
        return;
      }
    }

    if (r.ritual) {
      this.bus.emit('toast', r.memo || 'Ritual observed as printed.');
      if (typeof r.standing === 'number') {
        this.standing = r.standing;
        this.player.setDesignation(this.designation, `STANDING ${r.standing}`);
      }
    }

    if (r.review) {
      this.fsm.to('PERSONNEL_REVIEW');
      await personnelReviewLetter(this.deps, r.review, r.standing_delta || 0, r.standing || this.standing);
      if (typeof r.standing === 'number') this.standing = r.standing;
      if (typeof r.probation === 'number') {
        this.probation = r.probation;
        this.post.setProbation(this.probation > 0 ? 1 : 0);
        if (this.probation > 0) this.bus.emit('chyron', 'PROBATION. THRESHOLDS ARE RAISED. THE PALETTE HAS BEEN INFORMED.');
      }
      if (r.grade_changed && r.grade) await gradeChangeCeremony(this.deps, this.designation, r.grade);
      for (const s of r.new_stamps || []) this.bus.emit('toast', `STAMP AFFIXED TO YOUR FILE: ${s}`);
      if (r.purged) {
        await this.enterPurge();
        return;
      }
      this.player.setDesignation(this.designation, `STANDING ${this.standing}`);
    }

    this.busy = false;

    if (r.docket_cleared) {
      await docketClearedCeremony(this.deps, r.clearance_delta ?? 2);
      this.dom.showDocketCleared();
      if (this.firstDocket && this.profile.claim_phrase && this.profile.ticket_no) {
        await certificateCeremony(this.deps, this.designation, this.profile.ticket_no, this.profile.claim_phrase);
        this.profile = saveProfile({ intake_done: true });
        this.firstDocket = false;
      }
      this.fsm.to('ROAM');
      await this.refreshDocket(); // a fresh docket is composed server-side
      this.advanceDocket();
      return;
    }
    this.fsm.to('ROAM');
    this.advanceDocket();
  }

  private slipOpts(tier: CeremonyTier, movedM: number, stoodStill: boolean) {
    return {
      tier,
      movedM,
      stoodStill,
      designation: this.designation,
      onStandingTick: (s: number) => this.player.setDesignation(this.designation, `STANDING ${s}`),
      onSlamPop: () => this.post.slamPop(),
    };
  }

  private async refreshDocket(): Promise<void> {
    const d = await api.docket();
    if (d.ok && d.data && !d.data.void) {
      this.absorbDocketResponse(d.data);
      await this.deliverPending(d.data.pending_verdicts);
    } else if (d.data?.void) {
      this.enterVoid(d.data.memo || 'This file is VOID.');
    }
  }

  /** Drawer-glow delivery: queued verdicts land as slips, one by one. */
  private async deliverPending(pending: Verdict[] | undefined): Promise<void> {
    if (!pending || !pending.length) return;
    this.bus.emit('toast', 'A VERDICT WAS FILED IN YOUR ABSENCE. IT IS BEING DELIVERED NOW.');
    for (const v of pending) {
      await this.slip.show(v, this.slipOpts(v.ceremony || 'EXPEDITED', 0, true));
      this.standing = v.standing;
      this.player.setDesignation(this.designation, `STANDING ${v.standing}`);
    }
  }

  // ---------------------------------------------------------------------------
  // Rituals at the lectern (M8)
  // ---------------------------------------------------------------------------

  private async lecternRitual(): Promise<void> {
    const slot = this.activeSlot;
    if (!slot || slot.type !== 'RITUAL' || !slot.ritual) {
      this.bus.emit('chyron', 'RITUALS ARE PERFORMED STANDING, IN STILLNESS. NONE IS ASSIGNED TO YOU AT THIS TIME.');
      return;
    }
    this.fsm.to('COMPOSE');
    const result = await this.compose.compose({
      formId: slot.form_id,
      demand: slot.demand_text,
      constraints: slot.constraints,
      designation: this.designation,
      mode: slot.ritual.kind,
      ritual: slot.ritual,
      demandId: slot.demand_id,
      overheadAt: { x: LECTERN.x, z: LECTERN.z },
    });
    if (result.abandoned) {
      this.fsm.to('SUMMONS');
      this.bus.emit('chyron', 'THE RITUAL WAITS. RITUALS ARE PATIENT. THE LECTERN IS PATIENT. EVERYTHING HERE IS PATIENT.');
      return;
    }
    // rituals are zero-inference: submit without the latency ceremony
    this.busy = true;
    const res = await api.submit({
      demand_id: slot.demand_id,
      text: result.text,
      telemetry: result.telemetry,
      completed: result.completed === true,
    });
    this.busy = false;
    if (!res.ok || !res.data) {
      this.bus.emit('toast', res.memo || 'The ritual was not accepted. The interval resets.');
      this.fsm.to('SUMMONS');
      return;
    }
    await this.handleSubmitResponse(res.data, slot, 'EXPEDITED', 0, true);
  }

  // ---------------------------------------------------------------------------
  // Recess (M8) — 30 seconds, counted; abandonment quantified to the percent
  // ---------------------------------------------------------------------------

  private async recess(): Promise<void> {
    const assigned = this.activeSlot?.type === 'RECESS';
    this.busy = true;
    this.fsm.push('RECESS');
    const layer = printLayer();
    const card = document.createElement('div');
    card.className = 'paper-modal slim';
    card.innerHTML =
      `<div class="paper-head letterhead"><span>${assigned ? 'MANDATORY RECESS' : 'VOLUNTARY RECESS'}</span><span>30 SECONDS</span></div>`;
    const body = document.createElement('div');
    body.className = 'paper-body typewriter';
    body.style.textAlign = 'center';
    const count = document.createElement('div');
    count.style.cssText = 'font-size:34px;margin:8px 0;font-variant-numeric:tabular-nums';
    const note = document.createElement('div');
    note.className = 'compose-note';
    note.textContent = 'SIT. THE BUREAU IS COUNTING. LEAVING EARLY IS MEASURED.';
    body.append(count, note);
    card.appendChild(body);
    const foot = document.createElement('div');
    foot.className = 'paper-foot';
    foot.style.justifyContent = 'center';
    card.appendChild(foot);
    layer.appendChild(card);

    const t0 = performance.now();
    const TOTAL = 30;
    const outcome = await new Promise<boolean>((resolve) => {
      let timer = 0;
      let onKey: (e: KeyboardEvent) => void = () => undefined;
      const settle = (v: boolean): void => {
        window.clearInterval(timer);
        window.removeEventListener('keydown', onKey);
        resolve(v);
      };
      const leave = document.createElement('button');
      leave.type = 'button';
      leave.className = 'slam-btn secondary';
      leave.textContent = 'ABANDON REST';
      leave.addEventListener('click', () => settle(false));
      foot.appendChild(leave);
      onKey = (e: KeyboardEvent): void => {
        if (['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Escape'].includes(e.code)) {
          settle(false);
        }
      };
      window.addEventListener('keydown', onKey);
      timer = window.setInterval(() => {
        const left = TOTAL - (performance.now() - t0) / 1000;
        count.textContent = Math.max(0, Math.ceil(left)).toString();
        if (left <= 0) settle(true);
      }, 200);
    });
    card.remove();
    const elapsed = Math.round(performance.now() - t0);
    const res = await api.recess(outcome, elapsed);
    this.fsm.pop();
    this.busy = false;
    if (res.data) {
      this.bus.emit('toast', res.data.memo);
      if (typeof res.data.standing === 'number') {
        this.standing = res.data.standing;
        this.player.setDesignation(this.designation, `STANDING ${this.standing}`);
      }
      if (res.data.docket) this.docket = res.data.docket;
      if (res.data.docket_cleared) {
        await docketClearedCeremony(this.deps, 2);
        await this.refreshDocket();
      }
    } else {
      this.bus.emit('toast', res.memo || 'The bench filed no complaint.');
    }
    this.advanceDocket();
  }

  // ---------------------------------------------------------------------------
  // Absence accounting (M9): the turnstile holds FORM 7A-ABS in its door slot
  // ---------------------------------------------------------------------------

  private async absenceForm(): Promise<void> {
    const slot = this.docket?.slots[0];
    if (!slot || slot.type !== 'ABS' || slot.status !== 'PENDING') {
      this.absenceLocked = false;
      return;
    }
    this.activeSlot = slot;
    this.fsm.to('COMPOSE');
    const result = await this.compose.compose({
      formId: slot.form_id,
      demand: slot.demand_text,
      constraints: slot.constraints,
      designation: this.designation,
      mode: 'standard',
      demandId: slot.demand_id,
      draft: this.drafts.get(slot.demand_id),
      submitLabel: 'FILE THROUGH THE DOOR SLOT',
    });
    if (result.abandoned) {
      this.drafts.set(slot.demand_id, result.text);
      this.fsm.to('ABSENCE_ACCOUNTING');
      this.bus.emit('chyron', 'DRAFT ABANDONED. The Bureau read it anyway. The turnstile remains a turnstile.');
      return;
    }
    this.busy = true;
    this.fsm.to('ADJUDICATION_FULL');
    const request = api.submit({ demand_id: slot.demand_id, text: result.text, telemetry: result.telemetry });
    const outcome = await this.adjudication.run('FULL', request);
    if (outcome.kind === 'result' && outcome.response?.ok && outcome.response.data) {
      this.absenceLocked = false;
      setPrompt('turnstile', 'TURNSTILE — PROCEED');
      this.bus.emit('chyron', 'ABSENCE ACCOUNTED. THE TURNSTILE FORGIVES MECHANICALLY.');
      await this.handleSubmitResponse(outcome.response.data, slot, 'FULL', outcome.movedM, outcome.stoodStill,
        { text: result.text, telemetry: result.telemetry });
    } else if (outcome.kind === 'queued-timeout' ||
               (outcome.response && outcome.response.status === 202)) {
      this.absenceLocked = false;
      await this.slip.showQueuedChit('THE ABSENCE FORM IS QUEUED. THE TURNSTILE ACCEPTS THIS, GRUDGINGLY.');
      this.busy = false;
      this.fsm.to('ROAM');
      await this.refreshDocket();
      this.advanceDocket();
    } else {
      this.busy = false;
      this.fsm.to('ABSENCE_ACCOUNTING');
      this.bus.emit('toast', outcome.response?.memo || 'The door slot jammed. File again.');
    }
  }

  // ---------------------------------------------------------------------------
  // FORM 99-V (Window 4) — denied, denied, granted; the purge; the copier
  // ---------------------------------------------------------------------------

  private async form99v(): Promise<void> {
    this.busy = true;
    this.fsm.push('FORM99V');
    const result = await this.compose.compose({
      formId: 'FORM 99-V',
      demand: FORM99V_DEMAND,
      constraints: [],
      designation: this.designation,
      mode: 'standard',
      demandId: 'form99v:' + Date.now(),
      submitLabel: 'PETITION TO CEASE',
    });
    if (result.abandoned) {
      this.fsm.pop();
      this.busy = false;
      this.bus.emit('chyron', 'THE PETITION TO CEASE WAS NOT FILED. PROCESSING CONTINUES. IT ALWAYS DOES.');
      return;
    }
    this.fsm.to('ADJUDICATION_FULL');
    const request = api.form99v(result.text, result.telemetry);
    const outcome = await this.adjudication.run('FULL', request);
    if (outcome.kind !== 'result' || !outcome.response?.ok || !outcome.response.data) {
      this.fsm.pop();
      this.busy = false;
      this.bus.emit('toast', outcome.response?.memo || 'The petition sits in a tray. File it again presently.');
      return;
    }
    const r = outcome.response.data;
    const v = r.verdict;
    this.fsm.to('VERDICT');
    const undolly = this.adjudication.dollyIn();
    await this.slip.show(v, this.slipOpts('FULL', outcome.movedM, outcome.stoodStill));
    undolly();
    this.standing = v.standing;
    this.player.setDesignation(this.designation, `STANDING ${v.standing}`);
    if (r.granted) {
      await this.enterPurge();
      return;
    }
    this.fsm.pop();
    this.busy = false;
    this.bus.emit('chyron', 'PETITION TO CEASE: DENIED. THE BUREAU DECIDES WHEN YOU ARE FINISHED.');
    this.advanceDocket();
  }

  private async enterPurge(): Promise<void> {
    this.busy = true;
    this.voidFile = true;
    this.dom.setDocket(null);
    this.fsm.to('PURGE');
    await purgeCeremony(this.deps, this.designation);
    this.awaitCopier = true;
    setPrompt('copier', 'THE COPIER — WARM — E TO BE REISSUED');
    this.busy = false;
  }

  private enterVoid(memo: string): void {
    this.voidFile = true;
    this.awaitCopier = true;
    this.fsm.to('REISSUE');
    this.bus.emit('chyron', memo);
    this.bus.emit('context', 'THIS FILE IS VOID. THE COPIER IN THE REGISTRY CAN ARRANGE A SUCCESSOR.');
    setPrompt('copier', 'THE COPIER — WARM — E TO BE REISSUED');
  }

  private async copier(): Promise<void> {
    if (!this.awaitCopier || !this.voidFile) {
      this.bus.emit('chyron', 'THE COPIER IS COLD. IT COPIES ONLY THE VOID, AND YOU ARE STILL SOMEONE.');
      return;
    }
    this.busy = true;
    this.fsm.to('REISSUE');
    this.bus.emit('copier:reissue', {}); // the copier lights and sweeps the lid
    // the successor's fresh UUID, per contract — the old identity presents it
    const freshId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const res = await api.reissue(freshId);
    if (!res.ok || !res.data) {
      this.busy = false;
      this.bus.emit('toast', res.memo || 'The copier hums but produces nothing. Try again.');
      return;
    }
    const r = res.data;
    adoptIdentity(r.id);
    replaceProfile({
      uuid: r.id,
      ticket_no: r.number,
      designation: r.designation,
      standing: r.standing,
      grade: r.grade,
      form1_done: true,
      intake_done: true,
      audio_consent: this.profile.audio_consent,
      claim_phrase: this.profile.claim_phrase, // same number; phrase unchanged
    });
    this.profile = loadProfile();
    this.designation = r.designation;
    this.standing = r.standing;
    this.grade = r.grade;
    this.syncAvatarGrade();
    this.voidFile = false;
    this.awaitCopier = false;
    await reissueCeremony(this.deps, r);
    setPrompt('copier', 'THE COPIER — CURRENTLY COLD');
    this.busy = false;
    // the successor opens a session like any returning file; if the server
    // demands a fresh Form 1 from the copy, openSession routes there in voice
    await this.openSession();
    if (this.docket) this.advanceDocket();
  }

  // ---------------------------------------------------------------------------
  // The Archive drawer (casefile), and the room that is not in use
  // ---------------------------------------------------------------------------

  private async openDrawer(): Promise<void> {
    this.busy = true;
    if (this.drawerGlow && this.lastVerdictId) {
      // deferred Room-0 verdicts ride any /api/ack; a repeat ack still drains
      const res = await api.ack(this.lastVerdictId, 0, true);
      const deferred = res.data?.deferred_verdicts || [];
      for (const dv of deferred) {
        this.bus.emit('toast', `FROM A ROOM THAT IS NOT IN USE — ${dv.stamp}: ${dv.memo}`);
      }
      if (deferred.length) {
        this.drawerGlow = false;
        setPrompt('drawers', 'THE ARCHIVE — E TO LOCATE YOUR DRAWER');
      }
    }
    await openCasefile(this.bus, this.profile, {
      onReclaimed: async () => {
        this.profile = loadProfile();
        await this.openSession();
      },
    });
    this.busy = false;
  }

  private async enterRoom0(): Promise<void> {
    if (!this.room0) return;
    this.busy = true;
    const seeded = this.room0;
    const result = await offerRoom0(this.benchDeps, seeded);
    this.busy = false;
    this.room0 = null;
    document.body.dataset.room0 = '';
    setPrompt('door:form99', 'FORM-99 PROCESSING — E TO TRY IT');
    if (result.memo) this.bus.emit('toast', result.memo);
    if (result.kind === 'entered') {
      this.bus.emit('context', 'NO EXERCISE OCCURRED. THE VERDICT ABOUT IT WILL FIND YOUR DRAWER.');
    }
  }

  // ---------------------------------------------------------------------------

  /** Dress the avatar for its current grade (lanyard/clipboard). The grade
   *  ladder is GRADE_NAMES; PERSON (PROVISIONAL) is tier 0. */
  private syncAvatarGrade(): void {
    const tier = Math.max(0, GRADE_NAMES.indexOf(this.grade));
    this.player.setGradeTier(tier);
  }

  update(dt: number): void {
    // the walk across the lot owns the frame while it is active (approach prose,
    // chyron, door reach); nothing else in the director runs outside the walls.
    if (this.arrival?.isActive) this.arrival.update(dt);
    // the vertical core owns the frame while you are in the lift lobby / cab /
    // descent stub (chyron, riser, hotspots); nothing else in the director runs.
    if (this.vertcore?.isActive) this.vertcore.update(dt);
    this.adjudication.update(dt);
    this.director.update(dt);
    // the locked turnstile is a wall while absence is unaccounted
    if (this.absenceLocked && this.player.pos.x < TURNSTILE.x + 0.7) {
      this.player.pos.x = TURNSTILE.x + 0.7;
      this.player.vel.x = Math.max(0, this.player.vel.x);
    }
    void TUBE_MOUTH;
  }
}

// ---------------------------------------------------------------------------

function setPrompt(id: string, prompt: string): void {
  const it = INTERACTABLES.find((i) => i.id === id);
  if (it) it.prompt = prompt;
}

function textFocused(): boolean {
  const el = document.activeElement;
  return !!el && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT');
}
